import _ from 'lodash';
import { swapMapEntries } from '@/trendData/itemStore.utilities';
import { EMPTY_STRING_VALUE } from '@/trendData/trendData.constants';
import { type ActionsFactory, createStore, createStoreInstance } from '@seeqdev/utils';

/**
 * In-memory store for the values a scalar fetch returns. Like `itemRuntime.store.ts` these are
 * runtime-only and must never be persisted, but they are scalar-specific rather than shared
 * across item types, so they live in their own store.
 */

/** Y-value used to chart scalars whose value is not numeric (strings and booleans) */
export const NON_NUMERICAL_SCALAR_CHART_VALUE = 1;

export interface ScalarValues {
  /** The scalar's value; strings and booleans chart as NON_NUMERICAL_SCALAR_CHART_VALUE */
  value?: number | string | boolean | null;
  pointValue?: number | string | boolean | null;
  pointValueLower?: number;
  pointValueUpper?: number;
  /** Lower bound when the scalar is a shaded area */
  lower?: number;
  /** Upper bound when the scalar is a shaded area */
  upper?: number;
  /** Start of the display range the data was computed for */
  start?: number;
  /** End of the display range the data was computed for */
  end?: number;
  /** Chart samples spanning the display range: [time, value] or [time, lower, upper] pairs */
  data?: number[][];
  isStringSeries?: boolean;
  stringEnum?: Array<{ key: number; stringValue: string }>;
  valueUnitOfMeasure?: string;
}

export interface ScalarRuntimeState {
  /** Fetched values keyed by item id; scalars with no entry have not received results */
  values: Record<string, ScalarValues>;
}

export interface ScalarRuntimeActions {
  /**
   * Records the values carried by a scalar-results payload, deriving the chart data that spans
   * the display range; a value that is not numeric charts as NON_NUMERICAL_SCALAR_CHART_VALUE
   * with a stringEnum mapping it back to the value's text. Merges into the scalar's entry so
   * fields absent from the update keep their previous values, matching the item-cursor merge
   * the scalar store used to do. An invalid result clears the previous value, point value,
   * bounds, and string enum so a later merge cannot keep them.
   */
  setValues(
    id: string,
    payload: {
      value?: number | string | boolean;
      lower?: number;
      upper?: number;
      start?: number;
      end?: number;
      valueUnitOfMeasure?: string;
    },
  ): void;
  /**
   * Discards an item's values, when the item is removed or its fetch ends without data, so the
   * overlay stops applying stale results.
   */
  clearValues(id: string): void;
  /**
   * Moves entries to their swapped-in ids, mirroring the flux stores renaming items in place on
   * TREND_SWAP_ITEMS: the swapped-in item keeps rendering the previous values until its fetch
   * returns, and nothing is left keyed by an id that no longer exists.
   */
  swapValues(swaps: Record<string, string>): void;
  reset(): void;
}

export const initialState: ScalarRuntimeState = Object.freeze({
  values: {},
});

const STORE_NAME = 'ScalarRuntime';

const scalarRuntimeActionsFactory: ActionsFactory<ScalarRuntimeState, ScalarRuntimeActions> = (
  set,
  get,
  resetState,
) => ({
  setValues: (id, payload) => {
    const isStringSeries = _.isString(payload.value) || _.isBoolean(payload.value);
    const update: ScalarValues = {
      start: payload.start,
      end: payload.end,
    };
    let invalidResult = false;

    if (payload.valueUnitOfMeasure !== undefined) {
      update.valueUnitOfMeasure = payload.valueUnitOfMeasure;
    }

    if (isStringSeries || _.isFinite(payload.value)) {
      const value = isStringSeries ? NON_NUMERICAL_SCALAR_CHART_VALUE : (payload.value as number);
      update.pointValue = payload.value ?? null;
      update.value = payload.value ?? null;
      update.isStringSeries = isStringSeries;
      update.data = [
        [payload.start as number, value],
        [payload.end as number, value],
      ];
      if (isStringSeries) {
        update.stringEnum = [
          {
            key: NON_NUMERICAL_SCALAR_CHART_VALUE,
            stringValue: payload.value === '' ? EMPTY_STRING_VALUE : String(payload.value),
          },
        ];
      }
    } else if (
      _.isFinite(payload.lower) &&
      _.isFinite(payload.upper) &&
      (payload.lower as number) < (payload.upper as number)
    ) {
      const lower = payload.lower as number;
      const upper = payload.upper as number;
      update.pointValueLower = lower;
      update.pointValueUpper = upper;
      update.isStringSeries = false;
      update.lower = lower;
      update.upper = upper;
      update.data = [
        [payload.start as number, lower, upper],
        [payload.end as number, lower, upper],
      ];
    } else {
      invalidResult = true;
      update.isStringSeries = false;
      update.data = [];
      update.value = null;
      update.pointValue = null;
    }

    const current = get().values[id];
    const merged = { ...current, ...update };
    if (invalidResult) {
      delete merged.stringEnum;
      delete merged.lower;
      delete merged.upper;
      delete merged.pointValueLower;
      delete merged.pointValueUpper;
    }
    if (_.isEqual(current, merged)) {
      return;
    }
    set((draft) => {
      draft.values[id] = merged;
    });
  },

  clearValues: (id) => {
    if (!(id in get().values)) {
      return;
    }
    set((draft) => {
      delete draft.values[id];
    });
  },

  swapValues: (swaps) => {
    if (!Object.keys(swaps).some((swappedOutId) => swappedOutId in get().values)) {
      return;
    }
    set((draft) => {
      swapMapEntries(draft.values, swaps);
    });
  },

  reset: resetState,
});

const [ScalarRuntimeStoreProvider, useScalarRuntimeStoreInstance, useScalarRuntimeSelector] = createStore(
  STORE_NAME,
  initialState,
  scalarRuntimeActionsFactory,
);

export { ScalarRuntimeStoreProvider };

export const useScalarValues = () => useScalarRuntimeSelector((state) => state.values);

export const useScalarRuntimeActions = () => useScalarRuntimeStoreInstance().actions;

/**
 * @deprecated Legacy singleton bridge for non-React consumers (action files, utilities)
 * that cannot use hooks. Read state via `getState()` and mutate via `store.actions.*`.
 * New code should use the hooks above and the Provider.
 */
export function legacyCreateScalarRuntimeStore(initialStateOverrides?: Partial<ScalarRuntimeState>) {
  return createStoreInstance(
    initialStateOverrides ? { ...initialState, ...initialStateOverrides } : initialState,
    scalarRuntimeActionsFactory,
    { storeName: STORE_NAME },
  );
}
